import {
  ClientErrorResponse,
  ClientResponse,
  EmailIssueClient,
  EmailIssueClientUrls,
  SubmitEmailIssueParams,
} from './types';
import {
  Analytics,
  Errors,
  Metrics,
  initObservability,
  Observability,
  ObservabilityConfig,
} from './observability';
import {defaultClientFetch} from './clientFetch';

const CLIENT_TYPE = 'email';
const METRIC_TAGS = {client_type: CLIENT_TYPE};
const ERROR_TAGS = METRIC_TAGS;

export class DefaultEmailIssueClient implements EmailIssueClient {
  protected clientFetch: typeof fetch;

  protected urls?: Partial<EmailIssueClientUrls>;

  protected observability: Observability;

  constructor({
    clientFetch = defaultClientFetch,
    urls,
    observability,
  }: {
    clientFetch?: typeof fetch;
    urls?: Partial<EmailIssueClientUrls>;
    observability?: ObservabilityConfig;
  } = {}) {
    this.clientFetch = clientFetch;
    this.urls = urls;
    this.observability = initObservability(observability);
  }

  get analytics(): Analytics {
    return this.observability.analytics;
  }

  get errors(): Errors {
    return this.observability.errors;
  }

  get metrics(): Metrics {
    return this.observability.metrics;
  }

  async submitEmailIssue(
    params: SubmitEmailIssueParams,
  ): Promise<ClientResponse> {
    if (!this.urls?.emailIssueUrl) {
      throw new Error('missing email issue url');
    }

    const analyticsEventId = await this.analytics.requestAnalyticsEventId();

    const resp = await this.fetch(this.urls.emailIssueUrl, {
      method: 'POST',
      body: JSON.stringify({
        ...params,
        analyticsEventId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const json = await resp.json();

    if (json.type === 'error' && json.error) {
      return this.error(400, json.error);
    }

    if (json.type !== 'success') {
      return this.error(400, json.type);
    }

    return {code: 200, data: json};
  }

  protected error = async (
    code: number,
    error: string,
  ): Promise<ClientErrorResponse> => {
    if (code >= 500) {
      this.errors.reportError(error, {tags: ERROR_TAGS});
    }
    return Promise.resolve({code, error});
  };

  protected fetch = (url: RequestInfo | URL, init?: RequestInit) => {
    return this.clientFetch(url, init);
  };
}
